import { ChangeDetectionStrategy, Component, computed, input, signal } from '@angular/core';
import { RealmBrandingConfig } from '@/lib-backend';
import { sharedContentPreviewUrl } from '@/app/utils/shared-content-url';

@Component({
    selector: 'app-branding-preview',
    standalone: true,
    templateUrl: './branding-preview.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BrandingPreview {
    readonly branding = input.required<RealmBrandingConfig>();
    readonly domain = input.required<string>();

    protected readonly imageState = signal<'idle' | 'loaded' | 'error'>('idle');
    protected readonly imageDimensions = signal<{ width: number; height: number } | null>(null);

    protected readonly url = computed(() => `https://${this.domain()}`);

    protected readonly displayTitle = computed(
        () => this.branding().DisplayName?.trim() || 'Untitled brand'
    );

    protected readonly displayDescription = computed(
        () => this.branding().Description?.trim()
            || 'No description set — fill in SEO description above.'
    );

    protected readonly ogUrl = computed(() => this.resolve(this.branding().OgImageUrl));
    protected readonly faviconUrl = computed(() => this.resolve(this.branding().FaviconUrl));

    /**
     * Resolve a stored shared-content URL for preview: bucket-first, with the
     * realm domain as fallback when the bucket is not configured. Absolute URLs
     * pass through unchanged.
     */
    private resolve(url: string | null): string | null {
        if (!url?.trim()) return null;
        return sharedContentPreviewUrl(url);
    }

    protected readonly ogWarning = computed<string | null>(() => {
        if (!this.ogUrl()) return null;
        const dims = this.imageDimensions();
        if (!dims) return null;
        const ratio = dims.width / dims.height;
        const issues: string[] = [];
        if (dims.width < 1200) {
            issues.push(`width ${dims.width}px is below the recommended 1200px`);
        }
        if (Math.abs(ratio - 1.91) > 0.1) {
            issues.push(`aspect ${ratio.toFixed(2)}:1 deviates from the recommended 1.91:1`);
        }
        return issues.length > 0 ? issues.join(' · ') : null;
    });

    protected onImageLoad(event: Event): void {
        const img = event.target as HTMLImageElement;
        this.imageDimensions.set({ width: img.naturalWidth, height: img.naturalHeight });
        this.imageState.set('loaded');
    }

    protected onImageError(): void {
        this.imageState.set('error');
        this.imageDimensions.set(null);
    }
}
